import React, { useState, useMemo } from 'react'
import { Stack, Box, Alert, Typography } from '@mui/material'
import { useInventory } from '@/hooks/inventory/useInventory'
import { SearchHeader } from '../common'
import ReadyForTransportCards from '../views/ReadyForTransportCards'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { useLogistics } from '@/hooks/logistics/useLogistics'
import useAdminTransporter from '@/hooks/admin/useAdminTransporter'
import { Vehicle } from '@otw/models'
import { belongsOnNeedsTransportQueue, isRefinanceVehicle } from './needsTransportQueue'
import { buildUnmarkReadyForPickupUpdate } from '@/components/inventory/utils/vehicleFinancialToggleUpdates'

interface HiddenTransportMatch {
  id: string
  stockNumber: string
  vehicleLabel: string
  vin: string
  soldToName: string
}

const vehicleMatchesSearch = (vehicle: Vehicle, searchTerm: string): boolean => {
  const searchLower = searchTerm.toLowerCase().trim()
  if (!searchLower) return true

  const stockNumber = vehicle.stock_number?.toLowerCase() || ''
  const make = vehicle.make?.toLowerCase() || ''
  const model = vehicle.model?.toLowerCase() || ''
  const year = vehicle.year?.toString() || ''
  const vin = vehicle.vin?.toLowerCase() || ''
  const soldTo = vehicle.sold_to_user?.name?.toLowerCase() || ''

  return (
    stockNumber.includes(searchLower) ||
    make.includes(searchLower) ||
    model.includes(searchLower) ||
    year.includes(searchLower) ||
    vin.includes(searchLower) ||
    soldTo.includes(searchLower)
  )
}

const InventoryTab: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selectedPaidVehicles, setSelectedPaidVehicles] = useState<string[]>([])
  const [showHiddenVehicles, setShowHiddenVehicles] = useState<boolean>(false)

  const { vehicles: inventoryVehicles = [], updateDocument, updateDocumentAsync } = useInventory('master_inventory')
  const { addTransportTruck, isAddingTransportTruck, transportTrucks } = useLogistics()
  const { packages } = useAdminTransporter()

  const vehicleIdsInTransportFlow = useMemo(
    () => new Set(transportTrucks.map((truck) => truck.vehicle_id)),
    [transportTrucks]
  )
  const vehicleIdsInLegacyPackages = useMemo(() => {
    const ids = new Set<string>()
    packages.forEach((pkg) => {
      pkg.vehicle_ids?.forEach((id) => ids.add(id))
    })
    return ids
  }, [packages])

  const { showSuccess, showError } = useSnackbar()

  const paidVehiclesNeedingTransport = useMemo(() => {
    let filtered = inventoryVehicles.filter((vehicle) =>
      belongsOnNeedsTransportQueue(vehicle, {
        vehicleIdsInTransportFlow,
        vehicleIdsInLegacyPackages,
        showHiddenVehicles,
      }),
    )

    if (searchTerm.trim()) {
      filtered = filtered.filter((vehicle) => vehicleMatchesSearch(vehicle, searchTerm))
    }

    return filtered
  }, [inventoryVehicles, searchTerm, showHiddenVehicles, vehicleIdsInTransportFlow, vehicleIdsInLegacyPackages])

  const hiddenTransportMatches = useMemo<HiddenTransportMatch[]>(() => {
    if (!searchTerm.trim()) return []

    const matches: HiddenTransportMatch[] = []

    inventoryVehicles.forEach((vehicle) => {
      const isPaid = vehicle.paid_for === true
      const isReadyForPickup = vehicle.ready_for_transport?.ready_for_pickup === true
      const isReadyForTransport = isPaid || isReadyForPickup
      const inTransportFlow = vehicleIdsInTransportFlow.has(vehicle.id)
      const inLegacyPackage = vehicleIdsInLegacyPackages.has(vehicle.id)

      if (!isReadyForTransport || isRefinanceVehicle(vehicle)) return
      if (!inTransportFlow && !inLegacyPackage) return
      if (!vehicleMatchesSearch(vehicle, searchTerm)) return

      matches.push({
        id: vehicle.id,
        stockNumber: vehicle.stock_number || 'N/A',
        vehicleLabel: `${vehicle.year || 'N/A'} ${vehicle.make || ''} ${vehicle.model || ''}`.trim(),
        vin: vehicle.vin || 'N/A',
        soldToName: vehicle.sold_to_user?.name || 'No customer',
      })
    })

    return matches
  }, [inventoryVehicles, searchTerm, vehicleIdsInLegacyPackages, vehicleIdsInTransportFlow])

  const selectedVehicle = useMemo(
    () => paidVehiclesNeedingTransport.find((vehicle) => vehicle.id === selectedPaidVehicles[0]) ?? null,
    [paidVehiclesNeedingTransport, selectedPaidVehicles]
  )

  const hasFullPickupLocation = (vehicle: typeof selectedVehicle): boolean => {
    if (!vehicle?.pickup_location) return false
    const { address, city, state, zip } = vehicle.pickup_location
    return [address, city, state, zip].every((value) => Boolean(value?.trim()))
  }

  const shouldNotifyInventory =
    selectedPaidVehicles.length === 1 &&
    selectedVehicle != null &&
    !isRefinanceVehicle(selectedVehicle) &&
    !hasFullPickupLocation(selectedVehicle)

  const handleAddTruckToTransportFlow = async () => {
    const vin = selectedVehicle?.vin?.trim()

    if (!selectedVehicle?.id || !vin || isAddingTransportTruck) return

    try {
      await addTransportTruck({ vehicleId: selectedVehicle.id, vin })
      showSuccess('Truck added to transport flow')
      setSelectedPaidVehicles([])
    } catch {
      // Error messaging is handled in useLogistics mutation.
    }
  }

  const handleNotifyInventory = () => {
    if (!selectedVehicle?.id || isRefinanceVehicle(selectedVehicle)) return

    updateDocument({
      id: selectedVehicle.id,
      data: {
        ready_for_transport: {
          ...selectedVehicle.ready_for_transport,
          needs_address: true,
          ready_for_pickup: selectedVehicle.ready_for_transport?.ready_for_pickup ?? false,
        },
      },
    })

    setSelectedPaidVehicles([])
  }

  const handleHideSelected = () => {
    if (selectedPaidVehicles.length === 0) return

    selectedPaidVehicles.forEach((vehicleId) => {
      const vehicle = paidVehiclesNeedingTransport.find((v) => v.id === vehicleId)
      if (!vehicle) return

      updateDocument({
        id: vehicleId,
        data: {
          ready_for_transport: {
            ...vehicle.ready_for_transport,
            hidden_from_needs_transport: true,
          },
        },
      })
    })

    showSuccess(`${selectedPaidVehicles.length} truck${selectedPaidVehicles.length === 1 ? '' : 's'} hidden`)
    setSelectedPaidVehicles([])
  }

  const handleUnhideSelected = () => {
    if (selectedPaidVehicles.length === 0) return

    selectedPaidVehicles.forEach((vehicleId) => {
      const vehicle = paidVehiclesNeedingTransport.find((v) => v.id === vehicleId)
      if (!vehicle) return

      updateDocument({
        id: vehicleId,
        data: {
          ready_for_transport: {
            ...vehicle.ready_for_transport,
            hidden_from_needs_transport: false,
          },
        },
      })
    })

    showSuccess(`${selectedPaidVehicles.length} truck${selectedPaidVehicles.length === 1 ? '' : 's'} unhidden`)
    setSelectedPaidVehicles([])
  }

  const handleUnmarkReadyForPickup = async (vehicle: Vehicle) => {
    try {
      await updateDocumentAsync({
        id: vehicle.id,
        data: buildUnmarkReadyForPickupUpdate(vehicle),
      })
      showSuccess(
        vehicle.paid_for
          ? 'Unmarked ready for pickup. This truck stays here because it is paid for.'
          : 'Unmarked ready for pickup',
      )
    } catch (error) {
      console.error('Error unmarking ready for pickup:', error)
      showError('Failed to unmark ready for pickup')
    }
  }

  const handleToggleHiddenVehicles = () => {
    setShowHiddenVehicles((prev) => !prev)
    setSelectedPaidVehicles([])
  }

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: (theme) => theme.zIndex.appBar - 1,
          backgroundColor: 'background.default',
          pt: 1,
        }}
      >
        <SearchHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search vehicles..."
          showSearch={true}
          showDashboard={false}
          showCreateButton={!showHiddenVehicles && selectedPaidVehicles.length === 1}
          createButtonText={shouldNotifyInventory ? 'Notify inventory' : 'Add truck'}
          onCreateClick={shouldNotifyInventory ? handleNotifyInventory : handleAddTruckToTransportFlow}
          showSecondaryButton={selectedPaidVehicles.length > 0}
          secondaryButtonText={showHiddenVehicles ? 'Unhide selected' : 'Hide selected'}
          secondaryButtonIcon={undefined}
          onSecondaryClick={showHiddenVehicles ? handleUnhideSelected : handleHideSelected}
          showReadyForSaleButton={selectedPaidVehicles.length === 0}
          readyForSaleButtonText={showHiddenVehicles ? 'Show active trucks' : 'Show hidden trucks'}
          onReadyForSaleClick={handleToggleHiddenVehicles}
        />
      </Box>

      {hiddenTransportMatches.length > 0 && (
        <Alert severity="info">
          <Typography variant="body2" sx={{ mb: 1 }}>
            {hiddenTransportMatches.length} matching truck{hiddenTransportMatches.length === 1 ? '' : 's'} already in the transport system:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {hiddenTransportMatches.slice(0, 8).map((match) => (
              <Typography component="li" variant="body2" key={match.id}>
                {match.stockNumber} | {match.vehicleLabel} | VIN: {match.vin} | {match.soldToName}
              </Typography>
            ))}
          </Box>
          {hiddenTransportMatches.length > 8 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Showing first 8 of {hiddenTransportMatches.length} matches.
            </Typography>
          )}
        </Alert>
      )}

      <ReadyForTransportCards
        vehicles={paidVehiclesNeedingTransport}
        onViewVehicle={(vehicle) => {
          // Could open vehicle details dialog here if needed
          console.log('View vehicle:', vehicle)
        }}
        onSelectionChange={(vehicles) => setSelectedPaidVehicles(vehicles.map((vehicle) => vehicle.id))}
        onUnmarkReadyForPickup={handleUnmarkReadyForPickup}
        selectedVehicleIds={selectedPaidVehicles}
      />
    </Stack>
  )
}

export default InventoryTab
